import { useEffect, useRef, useState, FormEvent, ReactNode } from 'react';
import { Send, Trash2, ExternalLink, MessageSquare } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { MediaItem, Role } from '@/types';

type Status = 'draft' | 'published' | 'archived';

type Message =
  | {
      role: 'user';
      content: string;
    }
  | {
      role: 'assistant';
      content: string;
      action: { type: string; url: string } | null;
      media: MediaItem[];
      meta: {
        tokens_used: number;
        latency_ms: number;
        filtered_workflow_count: number;
        total_workflow_count: number;
        permissions_used: string[];
        cache_hit?: boolean;
        cache_read_tokens?: number;
        cache_write_tokens?: number;
        input_tokens?: number;
        output_tokens?: number;
      };
    };

const STATUSES: Status[] = ['draft', 'published', 'archived'];

const TYPEWRITER_CHUNK = 3;
const TYPEWRITER_INTERVAL = 50;

export function TestPanel() {
  const [roles, setRoles]   = useState<Role[]>([]);
  const [role, setRole]     = useState<string>('');
  const [statuses, setStatuses] = useState<Set<Status>>(new Set(['draft', 'published']));
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.listRoles().then((r) => {
      setRoles(r);
      if (r.length > 0) setRole(r[0].name);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  function toggleStatus(s: Status) {
    setStatuses((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    setError(null);
    const userMsg: Message = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const history = messages.map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));

      const res = await api.testKb({
        message: trimmed,
        role,
        statuses: Array.from(statuses),
        history,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.text,
          action: res.action,
          media: res.media ?? [],
          meta: res.meta,
        },
      ]);
    } catch (e) {
      const msg = e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message;
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  function clearConversation() {
    setMessages([]);
    setError(null);
  }

  return (
    <div className="max-w-4xl flex flex-col h-[calc(100vh-152px)] md:h-[calc(100vh-4rem)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Test Panel</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Try messages against your knowledge base before publishing. Conversation is local — not persisted.
          </p>
        </div>
        {messages.length > 0 && (
          <button onClick={clearConversation} className="btn-secondary">
            <Trash2 size={15} className="mr-2" />
            Clear
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Spoof role</label>
            {roles.length === 0 ? (
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                No roles defined — add roles on the Roles page first.
              </p>
            ) : (
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {roles.map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.label || r.name}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Determines which workflows the agent can access via permissions.
            </p>
          </div>
          <div>
            <label className="label">Workflow statuses</label>
            <div className="flex gap-2 flex-wrap pt-1">
              {STATUSES.map((s) => (
                <label
                  key={s}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium cursor-pointer transition-colors ${
                    statuses.has(s)
                      ? 'bg-brand-50 dark:bg-brand-900/30 border-brand-300 dark:border-brand-700 text-brand-700 dark:text-brand-400'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={statuses.has(s)}
                    onChange={() => toggleStatus(s)}
                  />
                  {s}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Default: draft + published, so you can preview unpublished work.
            </p>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 overflow-y-auto mb-4 min-h-0"
      >
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <MessageSquare size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">No messages yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
              Type a question below to see how the agent responds with the current draft + published
              workflows. Try things like "How do I register a patient?" or something the agent
              shouldn't be able to help with.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {messages.map((m, i) => (
            <MessageBubble
              key={i}
              message={m}
              animate={m.role === 'assistant' && i === messages.length - 1}
            />
          ))}
          {sending && <ThinkingIndicator />}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={send} className="flex gap-2">
        <input
          type="text"
          className="input flex-1"
          placeholder="Ask the agent a question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
          autoFocus
        />
        <button type="submit" disabled={sending || !input.trim()} className="btn-primary">
          <Send size={16} className="mr-2" />
          Send
        </button>
      </form>
    </div>
  );
}

function ThinkingIndicator() {
  const [dots, setDots] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d % 3) + 1), 450);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 pl-1">
      <span className="inline-flex gap-[3px] items-center">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 transition-opacity duration-150"
            style={{ opacity: i < dots ? 1 : 0.2 }}
          />
        ))}
      </span>
      <span>Thinking</span>
    </div>
  );
}

function MessageBubble({ message, animate }: { message: Message; animate: boolean }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-brand-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%] text-sm">
          {message.content}
        </div>
      </div>
    );
  }

  return <AssistantBubble message={message} animate={animate} />;
}

type AssistantMessage = Extract<Message, { role: 'assistant' }>;

function AssistantBubble({
  message,
  animate,
}: {
  message: AssistantMessage;
  animate: boolean;
}) {
  const [displayedText, setDisplayedText] = useState(animate ? '' : message.content);
  const [done, setDone] = useState(!animate);

  useEffect(() => {
    if (!animate) return;
    const full = message.content;
    let pos = 0;
    let cancelled = false;

    const timer = setInterval(() => {
      if (cancelled) return;
      pos = Math.min(pos + TYPEWRITER_CHUNK, full.length);
      setDisplayedText(full.slice(0, pos));
      if (pos >= full.length) {
        clearInterval(timer);
        if (!cancelled) setDone(true);
      }
    }, TYPEWRITER_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stepMedia = message.media.filter((m) => typeof m.step_index === 'number');
  const floatMedia = message.media.filter((m) => typeof m.step_index !== 'number');

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%]">
        <div className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-relaxed">
          {renderLines(displayedText, stepMedia)}
          {!done && (
            <span
              className="inline-block w-[2px] h-[0.85em] bg-gray-500 dark:bg-gray-400 ml-[1px] align-middle"
              style={{ animation: 'blink 0.7s step-end infinite' }}
            />
          )}
        </div>

        {done && message.action && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-700 rounded-lg text-xs text-brand-700 dark:text-brand-400">
            <ExternalLink size={12} />
            <span className="font-medium">Would navigate to</span>
            <code className="font-mono">{message.action.url}</code>
          </div>
        )}

        {done && floatMedia.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-3 max-w-md">
            {floatMedia.map((m, i) => (
              <MediaPreview key={i} item={m} />
            ))}
          </div>
        )}

        {done && (
          <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 flex gap-2 flex-wrap items-center">
            <span>{message.meta.latency_ms} ms</span>
            <span>·</span>
            <span title={
              message.meta.input_tokens !== undefined
                ? `input: ${message.meta.input_tokens} · output: ${message.meta.output_tokens} · cached: ${message.meta.cache_read_tokens ?? 0} · written: ${message.meta.cache_write_tokens ?? 0}`
                : undefined
            }>
              {message.meta.tokens_used} tokens
            </span>
            <span>·</span>
            {message.meta.cache_hit === true && (
              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                ⚡ cache hit
                {message.meta.cache_read_tokens ? ` (${message.meta.cache_read_tokens} read)` : ''}
              </span>
            )}
            {message.meta.cache_hit === false && (
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium" title="Cache will be warm for the next request">
                ✎ cache write
                {message.meta.cache_write_tokens ? ` (${message.meta.cache_write_tokens} written)` : ''}
              </span>
            )}
            {message.meta.cache_hit === undefined && <span>no cache info</span>}
            <span>·</span>
            <span>
              {message.meta.filtered_workflow_count} of {message.meta.total_workflow_count} workflows visible
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function renderLines(text: string, stepMedia: MediaItem[]): ReactNode {
  const lines = text.split('\n');
  const nodes: ReactNode[] = [];
  let stepIdx = -1;

  lines.forEach((line, i) => {
    const isLast = i === lines.length - 1;
    const isComplete = !isLast || text.endsWith('\n');
    const isStep = /^\d+\.\s/.test(line);

    if (isStep) stepIdx++;

    if (line === '') {
      nodes.push(<div key={`l-${i}`} className="h-2" />);
    } else {
      nodes.push(
        <div key={`l-${i}`} className={i > 0 ? 'mt-0.5' : ''}>
          {renderInlineMarkdown(line)}
        </div>,
      );
    }

    if (isStep && isComplete) {
      const imgs = stepMedia.filter((m) => m.step_index === stepIdx);
      if (imgs.length > 0) {
        nodes.push(
          <div key={`sm-${stepIdx}`} className="my-2 grid grid-cols-1 gap-2 max-w-xs">
            {imgs.map((m, j) => (
              <MediaPreview key={j} item={m} />
            ))}
          </div>,
        );
      }
    }
  });

  return <>{nodes}</>;
}

function MediaPreview({ item }: { item: MediaItem }) {
  return (
    <figure className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
      {item.type === 'image' ? (
        <img src={item.url} alt={item.caption ?? ''} className="w-full h-32 object-cover" />
      ) : (
        <video src={item.url} controls className="w-full h-32 object-cover bg-black" />
      )}
      {item.caption && (
        <figcaption className="px-2 py-1.5 text-[11px] text-gray-700 dark:text-gray-300 leading-snug">
          {item.caption}
          {typeof item.step_index === 'number' && (
            <span className="text-gray-400 dark:text-gray-500"> · step {item.step_index + 1}</span>
          )}
        </figcaption>
      )}
    </figure>
  );
}

function renderInlineMarkdown(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}
